import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { metadataApi } from '@api/metadataApi';
import type {
  CreateDynamicDocumentRequest,
  CreateMetaDocumentTypeRequest,
  UpdateMetaDocumentTypeRequest,
  CreateMetaCatalogTypeRequest,
  UpdateMetaCatalogTypeRequest,
  FormEventHandlerDto,
} from '@/types/metadata';

export function useDocumentTypes() {
  return useQuery({
    queryKey: ['metadata', 'document-types'],
    queryFn: () => metadataApi.getDocumentTypes(true),
  });
}

export function useDocumentType(acqCode: string | undefined) {
  return useQuery({
    queryKey: ['metadata', 'document-types', acqCode],
    queryFn: () => metadataApi.getDocumentType(acqCode!),
    enabled: Boolean(acqCode),
  });
}

export function useDynamicDocuments(acqDocType: string | undefined) {
  return useQuery({
    queryKey: ['metadata', 'dynamic-documents', acqDocType],
    queryFn: () => metadataApi.getDynamicDocuments(acqDocType),
    enabled: Boolean(acqDocType),
  });
}

export function useDynamicDocument(acqId: string | undefined) {
  return useQuery({
    queryKey: ['metadata', 'dynamic-documents', 'by-id', acqId],
    queryFn: () => metadataApi.getDynamicDocument(acqId!),
    enabled: Boolean(acqId),
  });
}

/** Phase 8b: пошук DynamicDocument за Document.Id (для drill-down з ОСВ/Журналу). */
export function useDynamicDocumentByDocumentId(acqDocumentId: string | undefined) {
  return useQuery({
    queryKey: ['metadata', 'dynamic-documents', 'by-document', acqDocumentId],
    queryFn: () => metadataApi.getDynamicDocumentByDocumentId(acqDocumentId!),
    enabled: Boolean(acqDocumentId),
  });
}

export function useCreateDynamicDocument() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateDynamicDocumentRequest) => metadataApi.createDynamicDocument(acqReq),
    onSuccess: (_data, acqVars) => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'dynamic-documents', acqVars.docTypeCode] });
      message.success('Документ створено');
    },
    onError: () => message.error('Помилка створення документа'),
  });
}

export function usePostDynamicDocument() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => metadataApi.postDynamicDocument(acqId),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'dynamic-documents'] });
      message.success('Документ проведено, проводки сформовано');
    },
    onError: () => message.error('Помилка проведення'),
  });
}

export function usePreviewPosting() {
  return useMutation({
    mutationFn: (acqId: string) => metadataApi.previewPosting(acqId),
  });
}

export function useDeleteDynamicDocument() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => metadataApi.deleteDynamicDocument(acqId),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'dynamic-documents'] });
      message.success('Документ видалено');
    },
    onError: () => message.error('Помилка видалення'),
  });
}

// ── Constructor: CRUD for MetaDocumentType ───────────────────────
export function useCreateDocumentType() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateMetaDocumentTypeRequest) => metadataApi.createDocumentType(acqReq),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types'] });
      message.success('Тип документа створено');
    },
    onError: (acqErr: unknown) => {
      const acqMsg = typeof acqErr === 'object' && acqErr && 'response' in acqErr
        ? (acqErr as { response: { data: unknown } }).response?.data
        : 'Помилка створення';
      message.error(typeof acqMsg === 'string' ? acqMsg : 'Помилка створення типу');
    },
  });
}

export function useUpdateDocumentType() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: ({ acqId, acqReq }: { acqId: string; acqReq: UpdateMetaDocumentTypeRequest }) =>
      metadataApi.updateDocumentType(acqId, acqReq),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types'] });
      message.success('Тип документа оновлено');
    },
    onError: () => message.error('Помилка оновлення'),
  });
}

export function useDeleteDocumentType() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => metadataApi.deleteDocumentType(acqId),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types'] });
      message.success('Тип документа видалено');
    },
    onError: () => message.error('Помилка видалення (або існують документи цього типу)'),
  });
}

// ── Constructor: CRUD for MetaCatalogType ─────────────────────────
export function useCatalogTypes() {
  return useQuery({
    queryKey: ['metadata', 'catalog-types'],
    queryFn: () => metadataApi.getCatalogTypes(true),
  });
}

export function useCatalogType(acqCode: string | undefined) {
  return useQuery({
    queryKey: ['metadata', 'catalog-types', acqCode],
    queryFn: () => metadataApi.getCatalogType(acqCode!),
    enabled: Boolean(acqCode),
  });
}

export function useCreateCatalogType() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqReq: CreateMetaCatalogTypeRequest) => metadataApi.createCatalogType(acqReq),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'catalog-types'] });
      message.success('Тип довідника створено');
    },
    onError: (acqErr: unknown) => {
      const acqMsg = typeof acqErr === 'object' && acqErr && 'response' in acqErr
        ? (acqErr as { response: { data: unknown } }).response?.data
        : 'Помилка створення';
      message.error(typeof acqMsg === 'string' ? acqMsg : 'Помилка створення типу');
    },
  });
}

export function useUpdateCatalogType() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: ({ acqId, acqReq }: { acqId: string; acqReq: UpdateMetaCatalogTypeRequest }) =>
      metadataApi.updateCatalogType(acqId, acqReq),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'catalog-types'] });
      message.success('Тип довідника оновлено');
    },
    onError: () => message.error('Помилка оновлення'),
  });
}

export function useDeleteCatalogType() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => metadataApi.deleteCatalogType(acqId),
    onSuccess: () => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'catalog-types'] });
      message.success('Тип довідника видалено');
    },
    onError: () => message.error('Помилка видалення (або існують записи цього типу)'),
  });
}

export function useSetEventHandlers() {
  const acqQc = useQueryClient();
  return useMutation({
    mutationFn: ({ acqTypeId, acqHandlers }: { acqTypeId: string; acqHandlers: FormEventHandlerDto[]; acqTypeCode: string }) =>
      metadataApi.setEventHandlers(acqTypeId, acqHandlers),
    onSuccess: (_, vars) => {
      acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types', vars.acqTypeCode] });
      acqQc.invalidateQueries({ queryKey: ['metadata', 'document-types'] });
      message.success('Event handlers збережено');
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? e?.response?.data?.title ?? e.message),
  });
}
